import { IconName } from '@ardoq/icons';
import { ViewIds } from '@ardoq/api-types';
import { getRightMenuConfig } from 'viewSettings/getRightMenuConfig';
import { SettingsBar, settingsBarConsts } from '@ardoq/settings-bar';
import { viewHasLegend } from 'views/metaInfoTabs';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import {
  type ExportConfigParams,
  type SettingsConfig,
  SettingsType,
} from '@ardoq/view-settings';
import { createDropdownSliderOptionManager } from 'viewSettings/settingsHelper';
import { DropdownOptionType, Notifier } from '@ardoq/dropdown-menu';
import { DEGREES_OF_RELATIONSHIP } from '@ardoq/global-consts';
import type { SwimlaneViewSettings } from './types';
import { t, type I18nLocale } from '@ardoq/i18n';

const VIEW_ID = ViewIds.SWIMLANE;

const getSliderOption = createDropdownSliderOptionManager(
  VIEW_ID,
  new Map(),
  onViewSettingsUpdate
);

const getLeftMenuConfig = (
  viewSettings: SwimlaneViewSettings,
  openDegreesViewSetting: Notifier,
  i18nLocale: I18nLocale
): SettingsConfig[] => {
  return [
    {
      id: 'orderAlphabetically',
      label: t(
        i18nLocale,
        'tabview:swimlaneView.orderAlphabetically',
        'Order lanes alphabetically'
      ),
      iconName: IconName.SORT_ASC,
      isActive: viewSettings.orderAlphabetically,
      type: SettingsType.TOGGLE,
      onViewSettingsUpdate,
    },
    {
      id: settingsBarConsts.DEGREES_OF_RELATIONSHIP_SLIDER_MENU_ITEM_ID,
      label: DEGREES_OF_RELATIONSHIP,
      iconName: IconName.ACCOUNT_TREE,
      type: SettingsType.DROPDOWN,
      isKeepOpen: true,
      options: [
        getSliderOption({
          name: 'incomingDegreesOfRelationship',
          label: t(i18nLocale, 'tabview:swimlaneView.incoming', 'Incoming'),
          value: viewSettings.incomingDegreesOfRelationship,
          min: 0,
          max: 10,
          step: 1,
          type: DropdownOptionType.SLIDER,
        }),
        getSliderOption({
          name: 'outgoingDegreesOfRelationship',
          label: t(i18nLocale, 'tabview:swimlaneView.outgoing', 'Outgoing'),
          value: viewSettings.outgoingDegreesOfRelationship,
          min: 0,
          max: 11,
          step: 1,
          substituteMax: 99,
          type: DropdownOptionType.SLIDER,
        }),
      ],
      requestOpen: openDegreesViewSetting,
    },
    {
      id: 'isVertical',
      label: t(
        i18nLocale,
        'tabview:swimlaneView.orientVertically',
        'Orient lanes vertically'
      ),
      iconName: IconName.VERTICAL_ALIGN_BOTTOM,
      isActive: viewSettings.isVertical,
      type: SettingsType.TOGGLE,
      onViewSettingsUpdate,
    },
  ];
};

type SwimlaneSettingsBarProps = {
  legendOnClick: () => void;
  exports: ExportConfigParams;
  viewSettings: SwimlaneViewSettings;
  openDegreesViewSetting: Notifier;
  i18nLocale: I18nLocale;
};

const SwimlaneSettingsBar = ({
  legendOnClick,
  exports,
  viewSettings,
  openDegreesViewSetting,
  i18nLocale,
}: SwimlaneSettingsBarProps) => (
  <SettingsBar
    viewId={VIEW_ID}
    leftMenu={getLeftMenuConfig(
      viewSettings,
      openDegreesViewSetting,
      i18nLocale
    )}
    rightMenu={getRightMenuConfig({
      isViewpointMode: false,
      viewId: VIEW_ID,
      viewstate: viewSettings,
      exports,
      withLegend: viewHasLegend(VIEW_ID),
      knowledgeBaseLink: KnowledgeBaseLink.SWIMLANE,
      legendOnClick,
      onViewSettingsUpdate,
      i18nLocale,
    })}
    i18nLocale={i18nLocale}
  />
);

export { SwimlaneSettingsBar };
